import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowBigUp, Delete, CornerDownLeft, ChevronDown, Space } from 'lucide-react';
import { useKeyboard, KEYBOARD_ROLES } from '../../context/KeyboardContext';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

// Definizione delle righe di lettere per ogni layout.
const LAYOUTS = {
  qwerty: [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
  ],
  qwertz: [
    ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['y', 'x', 'c', 'v', 'b', 'n', 'm'],
  ],
  azerty: [
    ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['w', 'x', 'c', 'v', 'b', 'n'],
  ],
  qzerty: [
    ['q', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'],
    ['w', 'x', 'c', 'v', 'b', 'n'],
  ],
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const TEXT_INPUT_TYPES = ['text', 'search', 'url', 'email', 'password'];

const isEditableTarget = (el) => {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (el.disabled || el.readOnly) return false;
  if (el.getAttribute && el.getAttribute('data-no-virtual-kb') != null) return false;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    if (!TEXT_INPUT_TYPES.includes(type)) return false;
    const im = (el.getAttribute('inputmode') || '').toLowerCase();
    if (im === 'numeric' || im === 'decimal' || im === 'tel') return false;
    return true;
  }
  return false;
};

// Scrive nel campo controllato React in modo che lo stato si aggiorni.
const setNativeValue = (el, value) => {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const desc = Object.getOwnPropertyDescriptor(proto, 'value');
  if (desc && desc.set) desc.set.call(el, value);
  else el.value = value;
};

const canUseSelection = (el) => {
  try { return typeof el.selectionStart === 'number'; } catch { return false; }
};

const insertText = (el, text) => {
  if (!el) return;
  if (canUseSelection(el)) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    setNativeValue(el, el.value.slice(0, start) + text + el.value.slice(end));
    el.dispatchEvent(new Event('input', { bubbles: true }));
    const pos = start + text.length;
    try { el.setSelectionRange(pos, pos); } catch { /* noop */ }
  } else {
    setNativeValue(el, (el.value || '') + text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

const backspace = (el) => {
  if (!el) return;
  if (canUseSelection(el)) {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    if (start === end) {
      if (start === 0) return;
      setNativeValue(el, el.value.slice(0, start - 1) + el.value.slice(end));
      el.dispatchEvent(new Event('input', { bubbles: true }));
      try { el.setSelectionRange(start - 1, start - 1); } catch { /* noop */ }
    } else {
      setNativeValue(el, el.value.slice(0, start) + el.value.slice(end));
      el.dispatchEvent(new Event('input', { bubbles: true }));
      try { el.setSelectionRange(start, start); } catch { /* noop */ }
    }
  } else {
    setNativeValue(el, (el.value || '').slice(0, -1));
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
};

const submitEnter = (el) => {
  if (!el) return;
  el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', keyCode: 13, which: 13, bubbles: true }));
  if (el.form && el.tagName !== 'TEXTAREA') {
    // niente submit automatico per evitare invii indesiderati; solo evento keydown
  }
};

/**
 * Tastiera a comparsa grande, agganciata al fondo dello schermo.
 * Compare quando si clicca un campo di TESTO (non numerico) e la funzione è
 * attiva per il ruolo corrente. Scrive nel campo mantenendo il focus.
 */
export const VirtualKeyboard = () => {
  const { enabled, layout } = useKeyboard();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [shift, setShift] = useState(false);
  const activeRef = useRef(null);

  const active = enabled && KEYBOARD_ROLES.includes(user?.role);

  useEffect(() => {
    if (!active) { setVisible(false); return undefined; }

    // La tastiera compare SOLO su interazione reale (tap/click) su un campo,
    // non su focus automatico (es. autofocus all'apertura di una finestra).
    const onPointerUp = (e) => {
      const el = e.target && e.target.closest ? e.target.closest('input, textarea') : null;
      if (el && isEditableTarget(el)) {
        activeRef.current = el;
        setVisible(true);
      }
    };
    // Il focusin serve solo ad aggiornare il campo attivo per la digitazione,
    // ma non apre la tastiera da solo.
    const onFocusIn = (e) => {
      if (isEditableTarget(e.target)) activeRef.current = e.target;
    };
    const onFocusOut = () => {
      setTimeout(() => {
        const a = document.activeElement;
        if (isEditableTarget(a)) return;             // passato ad un altro campo
        if (a && a.closest && a.closest('[data-virtual-keyboard]')) return; // click sui tasti
        setVisible(false);
      }, 120);
    };

    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [active]);

  const withEl = useCallback((fn) => {
    const el = activeRef.current || (isEditableTarget(document.activeElement) ? document.activeElement : null);
    if (el) { fn(el); try { el.focus(); } catch { /* noop */ } }
  }, []);

  const typeChar = useCallback((ch) => {
    withEl((el) => insertText(el, shift ? ch.toUpperCase() : ch));
  }, [shift, withEl]);

  if (!active || !visible) return null;

  const rows = LAYOUTS[layout] || LAYOUTS.qwerty;

  const Key = ({ label, onClick, className, testid, children, flex }) => (
    <button
      type="button"
      data-testid={testid}
      // Evita che il click rubi il focus dal campo attivo.
      onPointerDown={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'h-12 min-w-[2.4rem] rounded-lg border border-border bg-card text-lg font-semibold text-foreground shadow-sm-token select-none',
        'flex items-center justify-center transition-transform active:scale-[0.94] hover:bg-muted',
        flex && 'flex-1',
        className
      )}
    >
      {children ?? label}
    </button>
  );

  return (
    <div
      data-virtual-keyboard
      data-testid="virtual-keyboard"
      className="fixed inset-x-0 bottom-0 z-[200] border-t border-border bg-background/95 backdrop-blur px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-2xl"
    >
      <div className="mx-auto max-w-3xl flex flex-col gap-1.5">
        {/* Riga numeri */}
        <div className="flex gap-1.5">
          {DIGITS.map((d) => <Key key={d} label={d} testid={`vk-key-${d}`} flex onClick={() => typeChar(d)} />)}
        </div>
        {/* Righe lettere */}
        <div className="flex gap-1.5">
          {rows[0].map((k) => <Key key={k} testid={`vk-key-${k}`} flex onClick={() => typeChar(k)} label={shift ? k.toUpperCase() : k} />)}
        </div>
        <div className="flex gap-1.5 px-4">
          {rows[1].map((k) => <Key key={k} testid={`vk-key-${k}`} flex onClick={() => typeChar(k)} label={shift ? k.toUpperCase() : k} />)}
        </div>
        <div className="flex gap-1.5">
          <Key testid="vk-shift" onClick={() => setShift((s) => !s)} className={cn('px-3', shift && 'bg-primary text-primary-foreground border-primary')}>
            <ArrowBigUp className="h-5 w-5" />
          </Key>
          {rows[2].map((k) => <Key key={k} testid={`vk-key-${k}`} flex onClick={() => typeChar(k)} label={shift ? k.toUpperCase() : k} />)}
          <Key testid="vk-backspace" onClick={() => withEl(backspace)} className="px-3">
            <Delete className="h-5 w-5" />
          </Key>
        </div>
        {/* Riga azioni */}
        <div className="flex gap-1.5">
          <Key testid="vk-at" onClick={() => typeChar('@')} className="px-3">@</Key>
          <Key testid="vk-dot" onClick={() => typeChar('.')} className="px-3">.</Key>
          <Key testid="vk-space" onClick={() => withEl((el) => insertText(el, ' '))} flex className="text-muted-foreground">
            <Space className="h-5 w-5" />
          </Key>
          <Key testid="vk-comma" onClick={() => typeChar(',')} className="px-3">,</Key>
          <Key testid="vk-enter" onClick={() => withEl(submitEnter)} className="px-4">
            <CornerDownLeft className="h-5 w-5" />
          </Key>
          <Key testid="vk-hide" onClick={() => setVisible(false)} className="px-4">
            <ChevronDown className="h-5 w-5" />
          </Key>
        </div>
      </div>
    </div>
  );
};

export default VirtualKeyboard;
